import React, { useState } from 'react';
import { ApiResponseData } from '../utils/types';
import AffiliateCTAs from './AffiliateCTAs';

interface VisaResultProps {
  data: ApiResponseData;
}

// ── Status styles ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  emerald:'bg-emerald-50 text-emerald-700 border-emerald-200',
  blue:   'bg-blue-50 text-blue-700 border-blue-200',
  yellow: 'bg-amber-50 text-amber-700 border-amber-200',
  amber:  'bg-amber-50 text-amber-700 border-amber-200',
  red:    'bg-rose-50 text-rose-700 border-rose-200',
  rose:   'bg-rose-50 text-rose-700 border-rose-200',
};
function getStatusStyle(color: string) {
  return STATUS_STYLES[color?.toLowerCase()] ?? 'bg-slate-100 text-slate-700 border-slate-200';
}

// ── Entry checklist ───────────────────────────────────────────────────────────

interface ChecklistItem {
  label: string;
  detail: string;
  required: boolean; // true = always needed, false = recommended
}

function buildChecklist(visaType: string): ChecklistItem[] {
  const type = visaType.toLowerCase();
  const isVisaFree     = type.includes('visa-free') || type.includes('visa free');
  const isEVisa        = type.includes('evisa') || type.includes('e-visa');
  const isOnArrival    = type.includes('arrival');
  const isRequired     = type.includes('required') && !isOnArrival;

  const items: ChecklistItem[] = [
    {
      label: 'Valid passport',
      detail: isVisaFree
        ? 'Must be valid for the full duration of your stay.'
        : 'Most countries require 6 months validity beyond your stay.',
      required: true,
    },
    {
      label: 'Return or onward ticket',
      detail: 'Proof of departure is typically requested at immigration.',
      required: isVisaFree || isOnArrival,
    },
    {
      label: 'Proof of accommodation',
      detail: 'Hotel booking, Airbnb confirmation, or host invitation letter.',
      required: false,
    },
    {
      label: 'Proof of sufficient funds',
      detail: 'Bank statement or credit cards showing ability to fund your stay.',
      required: isVisaFree || isOnArrival,
    },
    {
      label: 'Travel insurance',
      detail: 'Some countries require it; always strongly recommended.',
      required: false,
    },
  ];

  if (isEVisa) {
    items.push({
      label: 'eVisa approval',
      detail: 'Apply online before travel. Processing can take 3–10 business days.',
      required: true,
    });
  }

  if (isOnArrival) {
    items.push(
      {
        label: 'Visa-on-arrival fee (cash)',
        detail: 'Exact amount in USD often required. Check current fee before departure.',
        required: true,
      },
      {
        label: 'Passport-size photos',
        detail: '2 recent photos, white background, 4×6 cm.',
        required: true,
      },
    );
  }

  if (isRequired) {
    items.push(
      {
        label: 'Visa application approved',
        detail: 'Apply via embassy or consulate well in advance — allow 4–8 weeks.',
        required: true,
      },
      {
        label: 'Passport-size photos',
        detail: '2–4 recent photos; check embassy requirements for exact specs.',
        required: true,
      },
    );
  }

  return items;
}

// ── Validity calculator ───────────────────────────────────────────────────────

function extractDays(duration: string): number | null {
  const match = duration.match(/^(\d+)\s*days?$/i);
  return match ? parseInt(match[1], 10) : null;
}

function VisaCalculator({ duration }: { duration: string }) {
  const [entryDate, setEntryDate] = useState('');
  const days = extractDays(duration);
  if (!days) return null;

  const today = new Date().toISOString().split('T')[0];

  let exitDate: Date | null = null;
  let daysUntilExit: number | null = null;
  let isExpired = false;

  if (entryDate) {
    exitDate = new Date(entryDate);
    exitDate.setDate(exitDate.getDate() + days);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    daysUntilExit = Math.ceil((exitDate.getTime() - now.getTime()) / 86400000);
    isExpired = daysUntilExit < 0;
  }

  return (
    <div className="px-6 py-5 border-b border-slate-100">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="font-bold text-slate-900 text-sm">Visa Validity Calculator</p>
      </div>

      <div className="pl-10 space-y-3">
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-500 font-medium w-28 flex-shrink-0">Planned entry date</label>
          <input
            type="date"
            value={entryDate}
            min={today}
            onChange={e => setEntryDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-100 focus:border-sky-400 text-slate-800"
          />
        </div>

        {exitDate && (
          <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-sky-600 font-semibold uppercase tracking-wider">Exit by</span>
              <span className="text-sm font-black text-slate-900">
                {exitDate.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-sky-600 font-semibold uppercase tracking-wider">Days remaining</span>
              <span className={`text-sm font-black ${isExpired ? 'text-rose-600' : daysUntilExit! <= 7 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {isExpired
                  ? `Expired ${Math.abs(daysUntilExit!)} days ago`
                  : daysUntilExit === 0
                    ? 'Exit today'
                    : `${daysUntilExit} days`}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const VisaResult: React.FC<VisaResultProps> = ({ data }) => {
  const { visa_rules, destination, passport, mandatory_registration, exception_rule, layover_rules } = data;
  const { primary_rule, secondary_rule } = visa_rules;
  const [checklistOpen, setChecklistOpen] = useState(true);

  const ruleLabel = secondary_rule ? `${primary_rule.name} / ${secondary_rule.name}` : primary_rule.name;
  const durationUsed = primary_rule.duration || secondary_rule?.duration || 'N/A';

  const exchangeValue = destination.exchange
    ? parseFloat(String(destination.exchange).replace(/[^\d.-]/g, ''))
    : null;
  const exchangeDisplay = exchangeValue && !isNaN(exchangeValue)
    ? exchangeValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    : 'N/A';

  const checklist = buildChecklist(primary_rule.name);

  return (
    <div id="visa-result-card" className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">

      {/* ── Header ── */}
      <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm mb-2">
            <span className="font-bold text-slate-900">{passport.code}</span>
            <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
            <span className="font-bold text-slate-900">{destination.code}</span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-500">{destination.name}</span>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-lg border text-sm font-bold ${getStatusStyle(primary_rule.color)}`}>
            {ruleLabel}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-400 no-print">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          Live data
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
        <div className="px-5 py-4">
          <p className="text-xs text-slate-400 font-medium mb-1">Max Stay</p>
          <p className="text-base font-bold text-slate-900">{durationUsed}</p>
        </div>
        <div className="px-5 py-4">
          <p className="text-xs text-slate-400 font-medium mb-1">Exchange Rate</p>
          <p className="text-base font-bold text-slate-900">
            {exchangeDisplay} <span className="text-xs font-normal text-slate-400">{destination.currency_code}</span>
          </p>
          <p className="text-[11px] text-slate-400">per 1 {passport.currency_code}</p>
        </div>
        <div className="px-5 py-4">
          <p className="text-xs text-slate-400 font-medium mb-1">Capital</p>
          <p className="text-base font-bold text-slate-900">{destination.capital}</p>
          <p className="text-[11px] text-slate-400">{destination.name}</p>
        </div>
      </div>

      {/* ── Affiliate CTAs ── */}
      <AffiliateCTAs
        visaType={primary_rule.name}
        passportCode={passport.code}
        destinationCode={destination.code}
        destinationName={destination.name}
      />

      {/* ── Validity calculator (only when numeric duration) ── */}
      {durationUsed && durationUsed !== 'N/A' && durationUsed !== 'Check with embassy' && (
        <VisaCalculator duration={durationUsed} />
      )}

      {/* ── Entry requirement checklist ── */}
      <div className="border-b border-slate-100">
        <button
          onClick={() => setChecklistOpen(o => !o)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors no-print"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-50 border border-violet-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <p className="font-bold text-slate-900 text-sm">Pre-Travel Checklist</p>
            <span className="text-[10px] font-semibold bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
              {checklist.length} items
            </span>
          </div>
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ${checklistOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Checklist always visible for print */}
        <div className={`${checklistOpen ? 'block' : 'hidden'} print:block`}>
          <div className="px-6 pb-5 space-y-3 pl-[3.75rem]">
            {checklist.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${item.required ? 'bg-violet-600' : 'bg-slate-200'}`}>
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 leading-tight">
                    {item.label}
                    {!item.required && (
                      <span className="ml-2 text-[10px] font-normal text-slate-400">recommended</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Optional sections ── */}
      {mandatory_registration && (
        <div className="px-6 py-5 border-b border-slate-100 flex items-start gap-4">
          <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <p className="font-bold text-slate-900 text-sm mb-0.5">Required: {mandatory_registration.name}</p>
            <p className="text-slate-500 text-xs leading-relaxed mb-3">
              Mandatory online registration required for entry. Typically submit within 72 hours of arrival.
            </p>
            <a
              href={mandatory_registration.link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-black text-white text-xs font-bold rounded-lg transition-all"
            >
              Submit Registration
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      )}

      {exception_rule && (
        <div className="px-6 py-5 border-b border-slate-100 flex items-start gap-4">
          <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="font-bold text-slate-900 text-sm mb-1">Special Condition: {exception_rule.name}</p>
            <p className="text-slate-500 text-xs leading-relaxed italic">"{exception_rule.notes}"</p>
          </div>
        </div>
      )}

      {layover_rules && (
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-violet-50 border border-violet-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <p className="font-bold text-slate-900 text-sm">Layover & Transit Rules</p>
          </div>
          <div className="space-y-3 pl-10">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-bold ${layover_rules.transit_visa_required ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
              {layover_rules.transit_visa_required ? 'Transit Visa Required' : 'Transit Visa Not Required'}
            </span>
            {layover_rules.visa_free_transit_hours && (
              <p className="text-xs text-slate-600"><span className="font-semibold">Visa-free transit:</span> Up to {layover_rules.visa_free_transit_hours} hours</p>
            )}
            <p className="text-xs text-slate-500 leading-relaxed">{layover_rules.notes}</p>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between no-print">
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Save as PDF
        </button>
        {secondary_rule?.link && (
          <a href={secondary_rule.link} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
            Official Portal
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
};

export default VisaResult;
